/** Derived, never-stored views over an account (05-architecture §2). */
import type { BotUser } from './state.js';
import type { ItemType } from './types.js';
import { dailyStatus } from './systems/daily.js';
import { nextEnergyAt } from './systems/energy.js';
import { unlockedTeamSlots } from './systems/levels.js';
import { missionEndsAt } from './systems/missions.js';
import { canUpgrade } from './systems/upgrade.js';
import { ENERGY_REGEN_MS } from './balance.js';

/** Energy after applying elapsed regeneration, without mutating the save. */
export function currentEnergy(save: BotUser, now: number): number {
  const e = save.adventures.energy;
  if (e.current >= e.max) return e.current;
  return Math.min(e.max, e.current + Math.floor((now - e.last) / ENERGY_REGEN_MS));
}

export function upgradableCount(save: BotUser, type: ItemType): number {
  return save.summoned[type].owned.filter((o) => {
    const c = canUpgrade(save, type, o);
    return c.cards && c.money;
  }).length;
}

export function ownedCount(save: BotUser, type: ItemType): number {
  return save.summoned[type].owned.filter((o) => o.total > 0).length;
}

export function hasOpenTeamSlot(save: BotUser): boolean {
  const slots = unlockedTeamSlots(save.level.level);
  const team = save.summoned.team.equiped;
  const anyOwnedCharacterFree = save.summoned.characters.owned.some(
    (o) => o.total > 0 && !team.some((s) => s.characters === o.id),
  );
  if (!anyOwnedCharacterFree) return false;
  for (let i = 0; i < slots; i++) if (!team[i]?.characters) return true;
  return false;
}

/** Epoch ms of the next +1 energy, counting regeneration a `tick` has not applied yet; null when full. */
export function nextEnergyAtLive(save: BotUser, now: number): number | null {
  return nextEnergyAt({ ...save, adventures: { ...save.adventures, energy: { ...save.adventures.energy, current: currentEnergy(save, now) } } }, now);
}

export interface Timer {
  id: 'energy' | 'mission' | 'daily';
  at: number;
}

/** Upcoming time-gated events, soonest first. */
export function upcomingTimers(save: BotUser, now: number): Timer[] {
  const out: Timer[] = [];
  const energy = nextEnergyAtLive(save, now);
  if (energy !== null) out.push({ id: 'energy', at: energy });
  const end = missionEndsAt(save);
  if (end !== null && end > now) out.push({ id: 'mission', at: end });
  const daily = dailyStatus(save, now);
  if (daily.state === 'claimed') out.push({ id: 'daily', at: daily.nextAt });
  return out.sort((a, b) => a.at - b.at);
}
